import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { AuthService } from '@auth/auth.service';
import * as authActions from '@auth/store/auth.actions';
import { DataService } from '@data/data.service';

import { ZGrid } from '@util/general/grid';
import { defaultGridOptions } from '@util/general/grid.util';

import * as gridColumnDefinitions from '../change-facility-grid/change-facility-grid.columns';

@Component({
  selector: 'app-change-facility-grid',
  templateUrl: './change-facility-grid.component.html',
  styleUrls: ['./change-facility-grid.component.scss'],
})
export class ChangeFacilityGridComponent extends ZGrid implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  public selectedRow: any = null;

  columnDefs = gridColumnDefinitions.columnDefs;
  rowData: any = [];
  filter: any;

  constructor(public dataService: DataService, private store: Store, private authService: AuthService) {
    super(dataService);

    this.gridOptions = {
      ...defaultGridOptions,
      rowSelection: 'multiple',
      onSelectionChanged: (_event) => this.publishSelectedRow(),
      onRowDoubleClicked: (_event) => this.selectRow(),
    };
  }

  ngOnInit() {
    combineLatest([this.dataService.facilityList$, this.authService.facilityDialogFilter$])
      .pipe(
        filter(([data, _flt]) => !!data),
        map(([data, flt]) => [data.list ? data.list : [], flt]),
        map(([data, flt]) => this.filterData(data, flt)),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => (this.rowData = data));

    this.authService.changeFacilityDialog$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.clearSelection());

    this.authService.selectedFacilityRow$.pipe(takeUntil(this.destroy$)).subscribe((row) => (this.selectedRow = row));
  }

  ngAfterViewInit() {
    this.agGrid.columnApi.applyColumnState({ state: gridColumnDefinitions.defColumnPreferences });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  private filterData(data: any, flt: any) {
    if (!flt || !flt.facility) {
      return data;
    }

    const returnData = data.filter((x) => {
      const idCheck = x.key.toLowerCase().includes(flt.facility.toLowerCase());
      const nameCheck = x.value.toLowerCase().includes(flt.facility.toLowerCase());
      return idCheck || nameCheck;
    });

    return returnData;
  }

  private clearSelection() {
    this.agGrid.api.deselectAll();
    this.authService.selectedFacilityRow$.next(null);
    this.authService.changeFacilityShowing.next('grid');
    this.authService.changeFacilityFilterMode.next(false);
  }

  publishSelectedRow() {
    const node = this.agGrid.api.getSelectedNodes().length > 0 ? this.agGrid.api.getSelectedNodes()[0].data : null;
    this.authService.selectedFacilityRow$.next(node);
  }

  dismissDialog() {
    this.dataService.hideScreen$.next(false);
    this.authService.changeFacilityDialog$.next(false);
  }

  selectRow() {
    if (this.selectedRow) {
      this.store.dispatch(new authActions.ChangeFacility(this.selectedRow.key));
      this.dismissDialog();
    }
  }
}
